// Administration — kit panels for users/audit, plus the two CdE-specific tabs:
// people & functions (people are not accounts) and the repository (provider
// status + catalogue health made visible).
import { useEffect, useState } from 'react';
import { ExternalLink, GitBranch, Plus, RefreshCw } from 'lucide-react';
import { UserManagementPanel } from 'auth-sso-kit/client';
import { AuditLogViewer } from 'audit-log-kit/client';
import { api } from '../api/client';
import { ConfirmModal } from '../components/ConfirmModal';
import { useAccent } from '../context/AccentContext';
import { useT } from '../i18n';
import { formatDateTime } from '../utils/format';

type Tab = 'users' | 'people' | 'repo' | 'audit';
const TABS: Tab[] = ['users', 'people', 'repo', 'audit'];
const ROLE_OPTIONS = ['admin', 'cde', 'governance', 'user'];
const ACCENT_PRESETS = ['#1a1682', '#0d7377', '#7c5cbf', '#c05580', '#4a5568'];

/** #/admin?tab=repo — GitHub sends the admin back to a specific tab. */
function initialTab(): Tab {
  const wanted = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('tab') as Tab | null;
  return wanted && TABS.includes(wanted) ? wanted : 'users';
}

export function AdminPage() {
  const { t } = useT();
  const [tab, setTab] = useState<Tab>(initialTab);
  return (
    <>
      <div className="page-header"><div><h1 className="page-title">{t('shell.title.admin')}</h1></div></div>
      <div className="tabs mb-24">
        {([['users', t('adm.tab.users')], ['people', t('adm.tab.people')], ['repo', t('adm.tab.repo')], ['audit', t('adm.tab.audit')]] as [Tab, string][]).map(([k, label]) => (
          <button key={k} className={`tab${tab === k ? ' active' : ''}`} onClick={() => setTab(k)}>{label}</button>
        ))}
      </div>
      {tab === 'users' && <UsersTab />}
      {tab === 'people' && <PeopleTab />}
      {tab === 'repo' && <RepoTab />}
      {tab === 'audit' && <section><AuditLogViewer /></section>}
    </>
  );
}

function UsersTab() {
  const { t } = useT();
  const { accent, setAccent } = useAccent();
  const [roles, setRoles] = useState<any[]>([]);
  const load = () => api.getUserRoles().then(setRoles).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <>
      <section className="mb-24"><UserManagementPanel /></section>

      <div className="card mb-16">
        <div className="card-header"><h3 className="card-title">{t('adm.roles.role')}</h3></div>
        <div className="p-16">
          <table className="table">
            <tbody>
              {roles.map(u => (
                <tr key={u.id}>
                  <td><span className="font-medium text-ink-1">{u.name}</span><div className="text-xs text-ink-3">{u.email}</div></td>
                  <td style={{ width: 160 }}>
                    <select className="form-select" value={u.role}
                            onChange={e => api.setUserRole(u.id, e.target.value).then(load)}>
                      {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3 className="card-title">{t('adm.appearance')}</h3></div>
        <div className="flex justify-between items-center p-16">
          <div>
            <div className="font-medium text-ink-1">{t('adm.accent')}</div>
            <div className="text-sm text-ink-3">{t('adm.accentDesc')}</div>
          </div>
          <div className="flex gap-8 items-center">
            {ACCENT_PRESETS.map(hex => (
              <span key={hex} className={`color-swatch ${accent === hex ? 'active' : ''}`}
                    style={{ background: hex }} onClick={() => setAccent(hex)} />
            ))}
            <input type="color" className="form-input" style={{ width: 32, height: 32, padding: 2 }}
                   value={accent} onChange={e => setAccent(e.target.value)} />
          </div>
        </div>
      </div>
    </>
  );
}

function PeopleTab() {
  const { t } = useT();
  const [people, setPeople] = useState<any[]>([]);
  const [functions, setFunctions] = useState<any[]>([]);
  const [newPerson, setNewPerson] = useState({ name: '', email: '', function_id: '' });
  const [newFunction, setNewFunction] = useState('');

  const load = () => {
    api.getPeople().then(setPeople).catch(() => {});
    api.getFunctions().then(setFunctions).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="two-column">
      <div className="card">
        <div className="card-header"><h3 className="card-title">{t('adm.people.title')}</h3></div>
        <div className="p-16">
          <table className="table table-compact">
            <thead>
              <tr><th>{t('adm.people.name')}</th><th>{t('adm.people.email')}</th><th>{t('adm.people.function')}</th><th>{t('adm.people.linked')}</th></tr>
            </thead>
            <tbody>
              {people.map(p => (
                <tr key={p.id}>
                  <td className="font-medium text-ink-1">{p.name}</td>
                  <td className="text-sm">{p.email ?? '—'}</td>
                  <td className="text-sm">{p.function_name ?? '—'}</td>
                  <td>{p.user_id ? <span className="badge badge-ok">✓</span> : <span className="text-ink-3">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex gap-8 mt-12" style={{ flexWrap: 'wrap' }}>
            <input className="form-input" placeholder={t('adm.people.name')} value={newPerson.name}
                   onChange={e => setNewPerson({ ...newPerson, name: e.target.value })} />
            <input className="form-input" placeholder={t('adm.people.email')} value={newPerson.email}
                   onChange={e => setNewPerson({ ...newPerson, email: e.target.value })} />
            <select className="form-select" style={{ width: 'auto' }} value={newPerson.function_id}
                    onChange={e => setNewPerson({ ...newPerson, function_id: e.target.value })}>
              <option value="">{t('adm.people.function')}</option>
              {functions.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <button className="btn btn-accent btn-sm" disabled={!newPerson.name.trim()}
                    onClick={() => api.createPerson(newPerson.name.trim(), {
                      email: newPerson.email || null,
                      function_id: newPerson.function_id ? Number(newPerson.function_id) : null,
                    }).then(() => { setNewPerson({ name: '', email: '', function_id: '' }); load(); })}>
              <Plus className="icon-sm" /> {t('adm.people.add')}
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3 className="card-title">{t('adm.functions.title')}</h3></div>
        <div className="p-16">
          <div className="flex flex-col gap-4">
            {functions.map(f => <div key={f.id} className="text-sm">{f.name}</div>)}
          </div>
          <div className="flex gap-8 mt-12">
            <input className="form-input" placeholder={t('adm.functions.title')} value={newFunction}
                   onChange={e => setNewFunction(e.target.value)} />
            <button className="btn btn-accent btn-sm" disabled={!newFunction.trim()}
                    onClick={() => api.createFunction(newFunction.trim()).then(() => { setNewFunction(''); load(); })}>
              <Plus className="icon-sm" /> {t('adm.functions.add')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RepoTab() {
  const { t, lang } = useT();
  const [index, setIndex] = useState<any | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [openHealth, setOpenHealth] = useState<string | null>(null);
  const [showTechnical, setShowTechnical] = useState(false);

  const load = () => api.getRepoIndex().then(idx => { setIndex(idx); setNotConfigured(false); }).catch(e => { if (e.status === 503) setNotConfigured(true); });
  useEffect(() => {
    load();
    fetch('/api/admin/settings', { credentials: 'include' }).then(r => r.ok ? r.json() : null)
      .then(s => setShowTechnical(s?.show_technical_files === 'true')).catch(() => {});
  }, []);

  const health = index?.health;
  const lists: Record<string, any[]> = index ? {
    empty: index.entries.filter((e: any) => e.isEmpty),
    duplicates: index.entries.filter((e: any) => e.isDuplicate),
    technical: index.entries.filter((e: any) => e.isTechnical),
    errors: index.entries.filter((e: any) => e.parseError),
  } : {};

  return (
    <>
      <GithubAppCard onChanged={load} />

      {notConfigured && (
        <div className="empty-state"><div className="empty-state-desc">{t('adm.repo.notConfigured')}</div></div>
      )}
      {!index && !notConfigured && <div className="page-loading"><div className="spinner" /></div>}

      {index && index.provider === 'local' && !index.writable && (
        <div className="card mb-16"><div className="p-16 text-sm text-warn">{t('adm.repo.localRo')}</div></div>
      )}

      {index && (
        <div className="card mb-16">
          <div className="card-header"><h3 className="card-title">{t('adm.tab.repo')}</h3></div>
          <div className="p-16">
            <div className="detail-row"><span className="detail-row-label">{t('adm.repo.provider')}</span> {index.provider}</div>
            <div className="detail-row"><span className="detail-row-label">{t('adm.repo.synced')}</span> {formatDateTime(index.builtAt, lang)}</div>
            <div className="detail-row">
              <span className="detail-row-label" />
              <button className="btn btn-sm" disabled={refreshing}
                      onClick={() => { setRefreshing(true); api.refreshRepoIndex().then(load).finally(() => setRefreshing(false)); }}>
                <RefreshCw className={`icon-sm${refreshing ? ' icon-spin' : ''}`} /> {t('adm.repo.resync')}
              </button>
            </div>
            <div className="detail-row" style={{ marginTop: 'var(--s8)' }}>
              <span className="detail-row-label">{t('adm.repo.showTechnical')}</span>
              <label className="toggle">
                <input type="checkbox" className="toggle-input" checked={showTechnical}
                       onChange={e => {
                         setShowTechnical(e.target.checked);
                         fetch('/api/admin/settings/show_technical_files', {
                           method: 'PUT', credentials: 'include',
                           headers: { 'Content-Type': 'application/json' },
                           body: JSON.stringify({ value: String(e.target.checked) }),
                         });
                       }} />
                <span className="toggle-slider" />
              </label>
            </div>
          </div>
        </div>
      )}

      {index && health && (
        <div className="card">
          <div className="card-header"><h3 className="card-title">{t('adm.repo.health')}</h3></div>
          <div className="p-16">
            <div className="flex gap-16" style={{ flexWrap: 'wrap' }}>
              <span className="text-sm">{t('adm.repo.health.total', { n: health.total })}</span>
              {([['empty', t('adm.repo.health.empty', { n: health.empty })],
                 ['duplicates', t('adm.repo.health.duplicates', { n: health.duplicates })],
                 ['technical', t('adm.repo.health.technical', { n: health.technical })],
                 ['errors', t('adm.repo.health.errors', { n: health.parseErrors })]] as [string, string][]).map(([k, label]) => (
                <button key={k} className="btn btn-ghost btn-sm" onClick={() => setOpenHealth(openHealth === k ? null : k)}>
                  {label}
                </button>
              ))}
            </div>
            {openHealth && (
              <div className="mt-12">
                {lists[openHealth].length === 0
                  ? <div className="text-sm text-ink-3">—</div>
                  : lists[openHealth].map((e: any) => (
                      <div key={e.path} className="detail-row">
                        <span className="rep-path" style={{ minWidth: 0 }}>{e.path}</span>
                        {e.parseError && <span className="text-xs text-warn">{e.parseError}</span>}
                      </div>
                    ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * The GitHub App this instance acts as. Created once from here through
 * GitHub's manifest flow (the browser posts a manifest to github.com, GitHub
 * hands the credentials back to /api/github/app/created), then installed on
 * the process repository. Below it: who has linked their own account.
 */
function GithubAppCard({ onChanged }: { onChanged: () => void }) {
  const { t, lang } = useT();
  const [app, setApp] = useState<any | null>(null);
  const [links, setLinks] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ key: string; params?: Record<string, string>; error?: boolean } | null>(null);
  const [confirmForget, setConfirmForget] = useState(false);

  const load = () => {
    api.getGithubApp().then(setApp).catch(() => {});
    api.getGithubLinks().then(setLinks).catch(() => setLinks([]));
  };
  useEffect(() => {
    load();
    // GitHub sends the admin back through #/admin?tab=repo&github=installed|norepo|error
    const [path, query] = window.location.hash.split('?');
    const params = new URLSearchParams(query ?? '');
    const gh = params.get('github');
    if (gh === 'installed') setFlash({ key: 'gh.app.flash.installed' });
    else if (gh === 'norepo') setFlash({ key: 'gh.app.norepo', error: true });
    else if (gh === 'error') setFlash({ key: 'gh.app.flash.error', params: { reason: params.get('reason') ?? '?' }, error: true });
    if (gh) history.replaceState(null, '', `${path}?tab=repo`);
  }, []);

  const repo = app?.repo?.full_name ?? '';

  // the manifest flow is a top-level POST to github.com — a plain form, not fetch
  const create = async () => {
    setBusy(true);
    try {
      const { action, manifest } = await api.getGithubManifest();
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = action;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'manifest';
      input.value = JSON.stringify(manifest);
      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
    } catch {
      setBusy(false);
    }
  };

  const refresh = async () => {
    setBusy(true);
    try { setApp(await api.refreshGithubApp()); onChanged(); } catch { /* shown by the state below */ } finally { setBusy(false); }
  };

  const forget = async () => {
    setConfirmForget(false);
    setBusy(true);
    try { await api.forgetGithubApp(); load(); onChanged(); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="card mb-16">
        <div className="card-header"><h3 className="card-title">{t('gh.app.title')}</h3></div>
        <div className="p-16">
          <p className="text-sm text-ink-3 mb-12">{t('gh.app.intro', { repo })}</p>
          {flash && (
            <div className="text-sm mb-12" style={{ color: flash.error ? 'var(--err)' : 'var(--ok)' }}>
              {t(flash.key, { repo, ...(flash.params ?? {}) })}
            </div>
          )}

          {!app ? (
            <div className="spinner" />
          ) : !app.configured ? (
            <>
              <div className="text-sm mb-8">{t('gh.app.notConfigured')}</div>
              <div className="text-xs text-ink-3 mb-12">{t('gh.app.createHint', { owner: app.repo.owner, repo })}</div>
              <button className="btn btn-accent btn-sm" disabled={busy} onClick={create}>
                <GitBranch className="icon-sm" /> {t('gh.app.create')}
              </button>
            </>
          ) : (
            <>
              <div className="detail-row">
                <span className="detail-row-label">{t('gh.app.slug')}</span>
                {app.html_url
                  ? <a href={app.html_url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{app.slug} <ExternalLink size={11} /></a>
                  : <span>{app.slug || `#${app.app_id}`}</span>}
                {app.source === 'env' && <span className="badge badge-plain">{t('gh.app.source.env')}</span>}
              </div>
              <div className="detail-row">
                <span className="detail-row-label">{t('gh.app.state')}</span>
                {app.installed
                  ? <span className="badge badge-ok">{t('gh.app.installed', { repo })}</span>
                  : <span className="badge badge-warn">{t('gh.app.notInstalled', { repo })}</span>}
              </div>
              {app.installed_at && (
                <div className="detail-row"><span className="detail-row-label">{t('gh.app.installedAt')}</span> {formatDateTime(app.installed_at, lang)}</div>
              )}
              {app.installation_repos?.length > 0 && (
                <div className="detail-row"><span className="detail-row-label">{t('gh.app.repos')}</span> {app.installation_repos.join(', ')}</div>
              )}
              {app.installed && <div className="text-xs text-ink-3 mt-8">{t('gh.app.mode')}</div>}
              <div className="flex gap-8 mt-12" style={{ flexWrap: 'wrap' }}>
                {!app.installed && app.install_url && (
                  <a className="btn btn-accent btn-sm" href={app.install_url}>{t('gh.app.install', { repo })}</a>
                )}
                <button className="btn btn-sm" disabled={busy} onClick={refresh}>
                  <RefreshCw className={`icon-sm${busy ? ' icon-spin' : ''}`} /> {t('gh.app.refresh')}
                </button>
                {app.source === 'db' && (
                  <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setConfirmForget(true)}>{t('gh.app.forget')}</button>
                )}
              </div>

              <h4 className="text-sm font-medium mt-16 mb-8">{t('gh.app.links')}</h4>
              {links.length === 0 ? (
                <div className="text-sm text-ink-3">{t('gh.app.noLinks')}</div>
              ) : (
                <table className="table table-compact">
                  <thead>
                    <tr><th>{t('gh.app.col.user')}</th><th>{t('gh.app.col.github')}</th><th>{t('gh.app.col.permission')}</th><th>{t('gh.app.col.since')}</th></tr>
                  </thead>
                  <tbody>
                    {links.map(l => (
                      <tr key={l.user_id}>
                        <td><span className="font-medium text-ink-1">{l.user_name}</span><div className="text-xs text-ink-3">{l.email}</div></td>
                        <td className="text-sm"><a href={l.html_url ?? '#'} target="_blank" rel="noreferrer">@{l.login}</a></td>
                        <td>
                          {l.status === 'expired'
                            ? <span className="badge badge-err">{t('gh.status.expired')}</span>
                            : <span className={`badge ${l.can_write ? 'badge-ok' : l.permission === 'read' ? 'badge-warn' : 'badge-err'}`}>{t(`gh.perm.${l.permission}`)}</span>}
                        </td>
                        <td className="text-sm">{formatDateTime(l.connected_at, lang)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>

      <ConfirmModal
        open={confirmForget}
        title={t('gh.app.forget')}
        message={t('gh.app.forgetConfirm')}
        confirmLabel={t('gh.app.forget')}
        destructive
        onConfirm={forget}
        onCancel={() => setConfirmForget(false)}
      />
    </>
  );
}
